import { jsPDF } from 'jspdf';

/**
 * Génère un certificat PDF de traçabilité pour un lot
 * @param {Object} lot - Les données du lot
 * @param {string} language - La langue (fr/en)
 */
export const generateTraceabilityCertificate = (lot, language = 'fr') => {
  const t = language === 'fr';
  const doc = new jsPDF();
  
  // Colors
  const greenPrimary = [26, 92, 56]; // #1A5C38
  const goldAccent = [245, 166, 35]; // #F5A623
  const grayText = [100, 100, 100];
  
  // Header background
  doc.setFillColor(...greenPrimary);
  doc.rect(0, 0, 210, 50, 'F');
  
  // Logo text (since we can't easily embed external images)
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(28);
  doc.setFont('helvetica', 'bold');
  doc.text('OCÉANA', 20, 25);
  
  // SmartTrace badge
  doc.setFontSize(12);
  doc.setTextColor(...goldAccent);
  doc.text('SmartTrace', 20, 35);
  
  // Certificate title
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(16);
  doc.text(t ? 'Certificat de Traçabilité' : 'Traceability Certificate', 20, 45);
  
  // Lot code on the right
  doc.setFontSize(10);
  doc.setTextColor(255, 255, 255);
  doc.text(lot.code, 190, 25, { align: 'right' });
  
  // Status badge
  const statusY = 35;
  if (lot.status === 'verified') {
    doc.setFillColor(34, 197, 94); // green
    doc.roundedRect(150, statusY - 5, 40, 10, 2, 2, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(9);
    doc.text(t ? 'VÉRIFIÉ' : 'VERIFIED', 170, statusY + 2, { align: 'center' });
  } else if (lot.status === 'recalled') {
    doc.setFillColor(239, 68, 68); // red
    doc.roundedRect(150, statusY - 5, 40, 10, 2, 2, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(9);
    doc.text(t ? 'RAPPELÉ' : 'RECALLED', 170, statusY + 2, { align: 'center' });
  }
  
  // Product section
  let yPos = 65;
  
  doc.setTextColor(...greenPrimary);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text(t ? 'Produit' : 'Product', 20, yPos);
  
  yPos += 8;
  doc.setTextColor(...grayText);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  
  const productName = lot.product.name[language] || lot.product.name.fr;
  doc.text(`${t ? 'Nom' : 'Name'}: ${productName}`, 20, yPos);
  yPos += 6;
  doc.text(`${t ? 'Variété' : 'Variety'}: ${lot.product.variety}`, 20, yPos);
  yPos += 6;
  doc.text(`${t ? 'Catégorie' : 'Category'}: ${lot.product.category[language] || lot.product.category.fr}`, 20, yPos);
  
  // Origin section
  yPos += 15;
  doc.setTextColor(...greenPrimary);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text(t ? 'Origine' : 'Origin', 20, yPos);
  
  yPos += 8;
  doc.setTextColor(...grayText);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  
  const countryName = lot.origin.country[language] || lot.origin.country.fr;
  doc.text(`${t ? 'Pays' : 'Country'}: ${countryName}`, 20, yPos);
  yPos += 6;
  doc.text(`${t ? 'Région' : 'Region'}: ${lot.origin.region}`, 20, yPos);
  yPos += 6;
  doc.text(`${t ? 'Producteur' : 'Producer'}: ${lot.origin.producer}`, 20, yPos);
  yPos += 6;
  doc.text(`${t ? 'Date de récolte' : 'Harvest date'}: ${lot.origin.harvestDate}`, 20, yPos);
  
  // Journey section
  yPos += 15;
  doc.setTextColor(...greenPrimary);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text(t ? 'Parcours du produit' : 'Product Journey', 20, yPos);
  
  yPos += 10;
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  
  // Table header
  doc.setFillColor(248, 242, 232); // #F8F2E8
  doc.rect(20, yPos - 5, 170, 8, 'F');
  doc.setTextColor(...greenPrimary);
  doc.setFont('helvetica', 'bold');
  doc.text(t ? 'Étape' : 'Step', 25, yPos);
  doc.text(t ? 'Lieu' : 'Location', 50, yPos);
  doc.text('Date', 130, yPos);
  doc.text(t ? 'Temp.' : 'Temp.', 160, yPos);
  
  yPos += 8;
  doc.setTextColor(...grayText);
  doc.setFont('helvetica', 'normal');
  
  lot.journey.forEach((step, index) => {
    if (yPos > 250) {
      doc.addPage();
      yPos = 20;
    }
    
    const locationText = step.location[language] || step.location.fr;
    const truncatedLocation = locationText.length > 40 ? locationText.substring(0, 37) + '...' : locationText;
    
    doc.text(`${step.step}`, 25, yPos);
    doc.text(truncatedLocation, 50, yPos);
    doc.text(step.date, 130, yPos);
    doc.text(step.temperature, 160, yPos);
    
    yPos += 7;
  });
  
  // Certifications section
  yPos += 10;
  doc.setTextColor(...greenPrimary);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('Certifications', 20, yPos);
  
  yPos += 8;
  doc.setTextColor(...grayText);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  
  lot.certifications.forEach((cert, index) => {
    doc.setFillColor(...goldAccent);
    doc.circle(25, yPos - 1.5, 1.5, 'F');
    doc.text(cert, 30, yPos);
    yPos += 6;
  });
  
  // Quality score
  yPos += 5;
  doc.text(`${t ? 'Score qualité' : 'Quality score'}: ${lot.qualityScore}/100`, 20, yPos);
  
  // Footer
  const footerY = 280;
  doc.setDrawColor(...greenPrimary);
  doc.setLineWidth(0.5);
  doc.line(20, footerY - 10, 190, footerY - 10);
  
  doc.setFontSize(8);
  doc.setTextColor(...grayText);
  doc.text(t 
    ? 'Ce certificat est généré automatiquement par le système SmartTrace d\'Océana.'
    : 'This certificate is automatically generated by Océana\'s SmartTrace system.', 
    105, footerY - 3, { align: 'center' }
  );
  doc.text(t 
    ? `Document généré le ${new Date().toLocaleDateString('fr-FR')} | oceana-ci.com`
    : `Document generated on ${new Date().toLocaleDateString('en-US')} | oceana-ci.com`,
    105, footerY + 3, { align: 'center' }
  );
  
  // Save the PDF
  const fileName = `Oceana_Traceability_${lot.code}_${new Date().toISOString().split('T')[0]}.pdf`;
  doc.save(fileName);
};

export default generateTraceabilityCertificate;
